import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, alpha } from "@mui/material";
import color from "../../Constants/colors";
import { useDashboardColors } from "./dashboardColors";

export interface LimitGaugeProps {
  label: string;
  used: number;
  // null = unlimited
  limit: number | null;
  unit?: string;
}

export const WARN_RATIO = 0.8;

// Semicircle geometry in SVG units; the gauge scales with its box.
const SIZE = 120;
const STROKE = 12;
const RADIUS = (SIZE - STROKE) / 2;
const CENTER_Y = SIZE / 2;
const ARC_LENGTH = Math.PI * RADIUS;
const ARC = `M ${STROKE / 2} ${CENTER_Y} A ${RADIUS} ${RADIUS} 0 0 1 ${
  SIZE - STROKE / 2
} ${CENTER_Y}`;
// Tick marks every 25% along the arc.
const TICKS = [0.25, 0.5, 0.75].map((share) => {
  const angle = Math.PI * (1 - share);
  const inner = RADIUS - STROKE / 2 - 3;
  const outer = RADIUS - STROKE / 2 - 7;
  return {
    x1: SIZE / 2 + inner * Math.cos(angle),
    y1: CENTER_Y - inner * Math.sin(angle),
    x2: SIZE / 2 + outer * Math.cos(angle),
    y2: CENTER_Y - outer * Math.sin(angle),
  };
});

/** A half-circle "power gauge" for one usage limit. */
const LimitGauge: React.FC<LimitGaugeProps> = ({
  label,
  used,
  limit,
  unit,
}) => {
  const { t } = useTranslation("home");
  const { accent } = useDashboardColors();
  const withUnit = (value: number) => (unit ? `${value} ${unit}` : `${value}`);

  const unlimited = limit === null;
  const ratio = unlimited ? 1 : limit === 0 ? 1 : Math.min(used / limit, 1);
  const reached = !unlimited && used >= limit;
  // Status colours only when the limit is close; otherwise the brand accent.
  const arcColor = reached
    ? color.red600
    : !unlimited && ratio >= WARN_RATIO
      ? color.orange600
      : accent;
  const percent = Math.round(ratio * 100);
  const gradientId = `gauge-${label.replace(/\W+/g, "-")}`;

  return (
    <Box
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={unlimited ? undefined : limit}
      aria-valuenow={used}
      aria-valuetext={
        unlimited
          ? `${withUnit(used)}, ${t("hub.limits.unlimited")}`
          : t("hub.limits.usedOf", {
              used: withUnit(used),
              limit: withUnit(limit),
            })
      }
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 0,
      }}
    >
      <Box sx={{ position: "relative", width: "100%", maxWidth: 120 }}>
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE / 2 + STROKE / 2}`}
          width="100%"
          style={{ display: "block", overflow: "visible" }}
          aria-hidden
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor={alpha(arcColor, 0.55)} />
              <stop offset="100%" stopColor={arcColor} />
            </linearGradient>
          </defs>
          <path
            d={ARC}
            fill="none"
            stroke={color.gray100}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
          {/* A zero-length round-capped stroke would still draw a dot. */}
          {ratio > 0 && (
            <path
              d={ARC}
              fill="none"
              stroke={unlimited ? alpha(accent, 0.25) : `url(#${gradientId})`}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={`${ARC_LENGTH * ratio} ${ARC_LENGTH}`}
              style={{ transition: "stroke-dasharray 0.8s ease" }}
            />
          )}
          {TICKS.map((tick, index) => (
            <line
              key={index}
              {...tick}
              stroke={color.gray300}
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          ))}
        </svg>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: unlimited ? 24 : 20,
              lineHeight: "24px",
              fontWeight: 700,
              color: reached ? color.red700 : color.gray900,
            }}
          >
            {unlimited ? "∞" : `${percent}%`}
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          mt: 0.75,
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 600,
          color: color.gray700,
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          lineHeight: "16px",
          color: reached ? color.red700 : color.gray500,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {unlimited
          ? `${withUnit(used)} · ${t("hub.limits.unlimited")}`
          : t("hub.limits.usedOf", {
              used: withUnit(used),
              limit: withUnit(limit),
            })}
      </Typography>
    </Box>
  );
};

export default LimitGauge;
